/**
 * StreamsMenu — nested nav dropdown for the public Navbar.
 *
 * Feb 2026 (landing revamp): the landing page was slimmed to Hero → About
 * → How → Pricing → Footer. Reference sections (Top Colleges, Branches,
 * CAP Rounds, NEET Explainer) moved OUT of the landing and INTO this
 * top-nav dropdown so students / parents can reach them from anywhere on
 * the site.
 *
 * Structure:
 *   Streams ▾
 *     ├─ Engineering   → hover reveals sub-menu on the right
 *     │    • Top Colleges     → /top-colleges?stream=engineering
 *     │    • Branches         → /engineering-branches
 *     │    • CAP Rounds Info  → /cap-rounds
 *     │    • FAQs             → (coming soon)
 *     └─ Medical (NEET)
 *          • Top Colleges     → /top-colleges?stream=medical
 *          • Explainer        → /guides/neet
 *          • FAQs             → (coming soon)
 *
 * Desktop: hover + focus opens; a small delay on mouseleave prevents
 * flicker when the pointer moves from the trigger into the panel.
 * Mobile: consumed as a collapsible group inside the burger drawer
 * (see Navbar.jsx MobileStreamsGroup) — this component is desktop-only.
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronRight, Building2, GitBranch, BookOpen, HelpCircle, HeartPulse, Cpu, Compass } from "lucide-react";

const ENGINEERING_ITEMS = [
  { label: "Top Colleges",      to: "/top-colleges?stream=engineering", Icon: Building2, testid: "streams-eng-colleges" },
  { label: "Branches",          to: "/engineering-branches",            Icon: GitBranch, testid: "streams-eng-branches" },
  { label: "CAP Rounds Info",   to: "/cap-rounds",                      Icon: Compass,  testid: "streams-eng-cap-rounds" },
  { label: "FAQs",              to: null, comingSoon: true,             Icon: HelpCircle, testid: "streams-eng-faqs" },
];

const MEDICAL_ITEMS = [
  { label: "Top Colleges",      to: "/top-colleges?stream=medical",     Icon: Building2, testid: "streams-med-colleges" },
  { label: "Explainer",         to: "/guides/neet",                     Icon: BookOpen, testid: "streams-med-explainer" },
  { label: "FAQs",              to: null, comingSoon: true,             Icon: HelpCircle, testid: "streams-med-faqs" },
];

const STREAMS = [
  { key: "engineering", label: "Engineering", sub: "MHT-CET · CAP",              Icon: Cpu,        items: ENGINEERING_ITEMS },
  { key: "medical",     label: "Medical",     sub: "NEET · MCC / State DME",     Icon: HeartPulse, items: MEDICAL_ITEMS },
];

export default function StreamsMenu() {
  const [open, setOpen] = useState(false);
  const [activeStream, setActiveStream] = useState("engineering");
  const closeTimer = useRef(null);
  const wrapRef = useRef(null);

  // Cancel any pending close when the pointer re-enters the trigger or
  // panel. Small (120ms) grace period so accidental cursor drift doesn't
  // slam the menu shut.
  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };
  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = setTimeout(() => setOpen(false), 120);
  };

  // Click-outside + Esc close.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDoc);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDoc);
    };
  }, [open]);

  const activeItems = (STREAMS.find((s) => s.key === activeStream) || STREAMS[0]).items;

  return (
    <div
      ref={wrapRef}
      className="relative"
      onMouseEnter={() => { cancelClose(); setOpen(true); }}
      onMouseLeave={scheduleClose}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        className="h-9 px-3 rounded-md text-[13px] font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-colors inline-flex items-center gap-1"
        data-testid="nav-streams-trigger"
      >
        Streams <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          className="absolute left-0 top-full mt-2 z-50 w-[520px] rounded-xl border border-slate-200 bg-white shadow-xl overflow-hidden animate-in fade-in slide-in-from-top-1 duration-150"
          role="menu"
          aria-label="Streams"
          data-testid="nav-streams-panel"
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
        >
          <div className="grid grid-cols-[180px,1fr]">
            {/* Left: stream picker */}
            <ul className="border-r border-slate-100 bg-slate-50 py-2">
              {STREAMS.map(({ key, label, sub, Icon }) => {
                const isActive = activeStream === key;
                return (
                  <li key={key}>
                    <button
                      type="button"
                      onMouseEnter={() => setActiveStream(key)}
                      onFocus={() => setActiveStream(key)}
                      onClick={() => setActiveStream(key)}
                      className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left transition-colors ${
                        isActive
                          ? "bg-white text-slate-900 border-r-2 border-r-amber-500"
                          : "text-slate-700 hover:bg-white/60"
                      }`}
                      data-testid={`nav-streams-${key}`}
                    >
                      <span className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${isActive ? "bg-amber-50 text-amber-800" : "bg-slate-100 text-slate-600"}`}>
                        <Icon className="w-4 h-4" />
                      </span>
                      <span className="min-w-0">
                        <span className="block text-[13px] font-semibold truncate">{label}</span>
                        <span className="block text-[10.5px] text-slate-500 truncate">{sub}</span>
                      </span>
                      <ChevronRight className={`w-3.5 h-3.5 ml-auto flex-shrink-0 ${isActive ? "text-amber-600" : "text-slate-300"}`} />
                    </button>
                  </li>
                );
              })}
            </ul>

            {/* Right: sub-menu for the active stream */}
            <ul className="py-2" data-testid={`nav-streams-items-${activeStream}`}>
              {activeItems.map((it) => {
                const inner = (
                  <>
                    <span className="w-7 h-7 rounded-md bg-slate-100 text-slate-600 flex items-center justify-center flex-shrink-0">
                      <it.Icon className="w-3.5 h-3.5" />
                    </span>
                    <span className="flex-1 text-[13.5px] font-semibold text-slate-900">{it.label}</span>
                    {it.comingSoon && (
                      <span className="text-[10px] font-bold uppercase tracking-widest text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-1.5 py-0.5">
                        Soon
                      </span>
                    )}
                  </>
                );
                if (it.comingSoon || !it.to) {
                  return (
                    <li key={it.label}>
                      <div
                        aria-disabled="true"
                        className="flex items-center gap-2.5 px-3 py-2.5 opacity-60 cursor-not-allowed"
                        data-testid={it.testid}
                      >
                        {inner}
                      </div>
                    </li>
                  );
                }
                return (
                  <li key={it.label}>
                    <Link
                      to={it.to}
                      onClick={() => setOpen(false)}
                      className="flex items-center gap-2.5 px-3 py-2.5 hover:bg-slate-50 transition-colors"
                      data-testid={it.testid}
                    >
                      {inner}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

/** Mobile burger-drawer version. Renders both stream groups as collapsible
 *  card groups so mobile parents don't need to hover to reach anything. */
export function MobileStreamsGroups({ onNavigate }) {
  const [openKey, setOpenKey] = useState("engineering");
  return (
    <div className="space-y-2" data-testid="nav-mobile-streams">
      {STREAMS.map(({ key, label, sub, Icon, items }) => {
        const isOpen = openKey === key;
        return (
          <div key={key} className="rounded-xl border border-slate-200 bg-white overflow-hidden">
            <button
              type="button"
              onClick={() => setOpenKey(isOpen ? null : key)}
              aria-expanded={isOpen}
              className="w-full flex items-center gap-3 px-3 py-3 text-left hover:bg-slate-50"
              data-testid={`nav-mobile-streams-${key}`}
            >
              <span className="w-9 h-9 rounded-lg bg-amber-50 text-amber-800 flex items-center justify-center flex-shrink-0">
                <Icon className="w-4 h-4" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block font-display text-sm font-semibold text-slate-900 truncate">{label}</span>
                <span className="block text-[11px] text-slate-500 truncate">{sub}</span>
              </span>
              <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform flex-shrink-0 ${isOpen ? "rotate-180" : ""}`} />
            </button>
            {isOpen && (
              <ul className="border-t border-slate-100 divide-y divide-slate-100">
                {items.map(({ label: sublabel, to, comingSoon, Icon: SubIcon, testid }) => {
                  const inner = (
                    <>
                      <SubIcon className="w-4 h-4 text-slate-500 flex-shrink-0" />
                      <span className="flex-1 text-[13px] font-semibold text-slate-800">{sublabel}</span>
                      {comingSoon && (
                        <span className="text-[10px] font-bold uppercase tracking-widest text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-1.5 py-0.5">
                          Soon
                        </span>
                      )}
                    </>
                  );
                  if (comingSoon || !to) {
                    return (
                      <li key={sublabel}>
                        <div className="flex items-center gap-2.5 px-3 py-2.5 opacity-60" data-testid={`mobile-${testid}`}>
                          {inner}
                        </div>
                      </li>
                    );
                  }
                  return (
                    <li key={sublabel}>
                      <Link
                        to={to}
                        onClick={() => onNavigate?.()}
                        className="flex items-center gap-2.5 px-3 py-2.5 hover:bg-slate-50 transition-colors"
                        data-testid={`mobile-${testid}`}
                      >
                        {inner}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
}
